import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  App,
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
  Spin,
  Table,
  Typography,
} from 'antd';
import { ArrowLeftOutlined, DeleteOutlined, PlusOutlined, SaveOutlined } from '@ant-design/icons';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import employeeService, { employeeKeys, type SalaryComponentRecord } from '@/services/employee.service';
import hrmsService, { hrmsKeys } from '@/services/hrms.service';
import { useEmployeeOptions } from './shared/useEmployeeOptions';

const { Text } = Typography;

type LineRow = {
  salary_component_id?: string;
  component_code?: string;
  component_name?: string;
  previous_amount?: number | null;
  amount?: number | null;
};

function isOpenComponent(c: SalaryComponentRecord, asOf = dayjs()): boolean {
  if (!c.effective_to) return true;
  return !dayjs(c.effective_to).isBefore(asOf, 'day');
}

function openComponentsByCode(rows: SalaryComponentRecord[]): Map<string, SalaryComponentRecord> {
  const map = new Map<string, SalaryComponentRecord>();
  const sorted = [...rows]
    .filter((c) => isOpenComponent(c))
    .sort((a, b) => dayjs(b.effective_from).valueOf() - dayjs(a.effective_from).valueOf());
  for (const c of sorted) {
    const code = String(c.component_code || '').trim().toUpperCase();
    if (!code || map.has(code)) continue;
    map.set(code, c);
  }
  return map;
}

const SalaryRevisionFormPage: React.FC = () => {
  const { id: editId } = useParams<{ id: string }>();
  const isEdit = Boolean(editId);
  const navigate = useNavigate();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const { options: empOptions } = useEmployeeOptions();
  const employeeId = Form.useWatch('employee_id', form);
  const linesWatch: LineRow[] = Form.useWatch('lines', form) || [];
  const hydratedEditRef = useRef(false);
  const [saving, setSaving] = useState(false);

  const {
    data: existing,
    isLoading: existingLoading,
    isError: existingError,
  } = useQuery({
    queryKey: hrmsKeys.salaryRevision(editId || ''),
    queryFn: () => hrmsService.getSalaryRevision(editId!),
    enabled: isEdit,
  });

  const { data: grades = [] } = useQuery({
    queryKey: hrmsKeys.grades(),
    queryFn: () => hrmsService.listGrades(),
  });

  const { data: salaryHeads = [], isLoading: headsLoading } = useQuery({
    queryKey: hrmsKeys.salaryHeads(),
    queryFn: () => hrmsService.listSalaryHeads(),
    staleTime: 60_000,
  });

  const { data: employeeComponents = [], isFetching: componentsLoading } = useQuery({
    queryKey: employeeKeys.salaryComponents(employeeId || ''),
    queryFn: () => employeeService.listSalaryComponents(employeeId!),
    enabled: Boolean(employeeId),
  });

  const { data: employee } = useQuery({
    queryKey: employeeKeys.detail(employeeId || ''),
    queryFn: () => employeeService.getById(employeeId!),
    enabled: Boolean(employeeId),
  });

  const earningHeads = useMemo(
    () =>
      (salaryHeads || [])
        .filter((h: any) => h.is_active !== false)
        .filter((h: any) => String(h.component_type || '').toLowerCase() !== 'deduction')
        .filter((h: any) => h.is_ot_head !== true),
    [salaryHeads]
  );

  const headOptions = useMemo(
    () =>
      earningHeads.map((h: any) => ({
        value: h.id,
        label: `${h.component_code} — ${h.component_name}`,
        head: h,
      })),
    [earningHeads]
  );

  const gradeOptions = grades
    .filter((g: any) => g.is_active !== false)
    .map((g: any) => ({
      value: g.id,
      label: `${g.grade_code} — ${g.grade_name}`,
    }));

  const newTotal = useMemo(
    () => (linesWatch || []).reduce((sum, line) => sum + (Number(line?.amount) || 0), 0),
    [linesWatch]
  );

  const previousTotal = useMemo(
    () => (linesWatch || []).reduce((sum, line) => sum + (Number(line?.previous_amount) || 0), 0),
    [linesWatch]
  );

  const linesFromRevision = (rev: any): LineRow[] => {
    const open = openComponentsByCode(employeeComponents);
    const raw = Array.isArray(rev?.lines_json) ? rev.lines_json : [];
    if (raw.length) {
      return raw.map((l: any) => {
        const code = String(l.component_code || '').toUpperCase();
        const head = earningHeads.find(
          (h: any) => String(h.component_code || '').toUpperCase() === code
        );
        const openComp = open.get(code);
        return {
          salary_component_id: head?.id,
          component_code: l.component_code || head?.component_code,
          component_name: l.component_name || head?.component_name,
          previous_amount: openComp ? Number(openComp.amount) || 0 : Number(rev?.previous_basic) || 0,
          amount: Number(l.amount) || 0,
        };
      });
    }
    // Legacy basic-only draft
    const basicHead = earningHeads.find(
      (h: any) => String(h.component_code || '').toUpperCase() === 'BASIC'
    );
    return [
      {
        salary_component_id: basicHead?.id,
        component_code: basicHead?.component_code || 'BASIC',
        component_name: basicHead?.component_name || 'Basic Salary',
        previous_amount: Number(rev?.previous_basic) || 0,
        amount: Number(rev?.new_basic) || 0,
      },
    ];
  };

  const buildDefaultLines = (): LineRow[] => {
    const open = openComponentsByCode(employeeComponents);
    if (open.size > 0) {
      return Array.from(open.values()).map((c) => {
        const head = earningHeads.find(
          (h: any) =>
            String(h.component_code || '').toUpperCase() === String(c.component_code || '').toUpperCase()
        );
        return {
          salary_component_id: head?.id,
          component_code: c.component_code,
          component_name: c.component_name || head?.component_name,
          previous_amount: Number(c.amount) || 0,
          amount: Number(c.amount) || 0,
        };
      });
    }

    const basicHead = earningHeads.find(
      (h: any) => String(h.component_code || '').toUpperCase() === 'BASIC'
    );
    const basicAmount = Number(employee?.basic_salary) || 0;
    if (basicHead) {
      return [
        {
          salary_component_id: basicHead.id,
          component_code: basicHead.component_code,
          component_name: basicHead.component_name,
          previous_amount: basicAmount,
          amount: basicAmount,
        },
      ];
    }
    return [
      {
        component_code: 'BASIC',
        component_name: 'Basic Salary',
        previous_amount: basicAmount,
        amount: basicAmount,
      },
    ];
  };

  // Hydrate edit form once draft + heads are ready
  useEffect(() => {
    if (!isEdit || !existing || headsLoading || hydratedEditRef.current) return;
    if (String(existing.status) !== 'Draft') {
      message.warning('Only Draft salary revisions can be edited');
      navigate(`/hr/salary-revisions/${editId}`, { replace: true });
      return;
    }
    // Wait for employee components so previous amounts are accurate (optional soft wait)
    if (componentsLoading) return;
    hydratedEditRef.current = true;
    form.setFieldsValue({
      employee_id: existing.employee_id,
      revision_type: existing.revision_type || 'Increment',
      effective_from: existing.effective_from ? dayjs(existing.effective_from) : undefined,
      new_grade_id: existing.new_grade_id || undefined,
      reason: existing.reason || undefined,
      lines: linesFromRevision(existing),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, existing, headsLoading, componentsLoading, earningHeads.length, employeeComponents]);

  // Prefill lines on create when employee changes
  useEffect(() => {
    if (isEdit) return;
    if (!employeeId || headsLoading || componentsLoading) return;
    form.setFieldsValue({ lines: buildDefaultLines() });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId, componentsLoading, headsLoading, employeeComponents, employee, earningHeads.length]);

  const onHeadChange = (index: number, headId: string) => {
    const head = earningHeads.find((h: any) => h.id === headId);
    if (!head) return;
    const code = String(head.component_code || '').toUpperCase();
    const open = openComponentsByCode(employeeComponents);
    const existingComp = open.get(code);
    const lines = [...(form.getFieldValue('lines') || [])] as LineRow[];
    const current = lines[index] || {};
    lines[index] = {
      ...current,
      salary_component_id: head.id,
      component_code: head.component_code,
      component_name: head.component_name,
      previous_amount: existingComp
        ? Number(existingComp.amount) || 0
        : Number(current.previous_amount) || 0,
      amount: current.amount != null ? current.amount : existingComp ? Number(existingComp.amount) || 0 : 0,
    };
    form.setFieldsValue({ lines });
  };

  const usedHeadIds = useMemo(
    () => new Set((linesWatch || []).map((l) => l.salary_component_id).filter(Boolean) as string[]),
    [linesWatch]
  );

  const buildPayload = (v: any) => {
    const lines: LineRow[] = (v.lines || []).filter(
      (l: LineRow) => l?.component_code && l.amount != null && Number(l.amount) >= 0
    );
    if (!lines.length) {
      throw new Error('Add at least one salary head with an amount');
    }
    const codes = lines.map((l) => String(l.component_code).toUpperCase());
    if (new Set(codes).size !== codes.length) {
      throw new Error('Each salary head can only appear once');
    }
    const basicLine = lines.find((l) => String(l.component_code).toUpperCase() === 'BASIC');
    const newBasic = basicLine != null ? Number(basicLine.amount) || 0 : null;
    const previousBasic =
      basicLine != null
        ? Number(basicLine.previous_amount) || 0
        : Number(employee?.basic_salary) || 0;

    return {
      employee_id: v.employee_id,
      revision_type: v.revision_type,
      effective_from: v.effective_from.format('YYYY-MM-DD'),
      new_grade_id: v.new_grade_id || null,
      previous_basic: previousBasic,
      new_basic: newBasic,
      reason: v.reason || null,
      lines_json: lines.map((l) => ({
        component_code: l.component_code,
        component_name: l.component_name,
        amount: Number(l.amount) || 0,
      })),
    };
  };

  const save = async () => {
    setSaving(true);
    try {
      const v = await form.validateFields();
      const payload = buildPayload(v);
      if (isEdit && editId) {
        await hrmsService.updateSalaryRevision(editId, payload);
        message.success('Salary revision updated');
        await queryClient.invalidateQueries({ queryKey: hrmsKeys.salaryRevision(editId) });
        await queryClient.invalidateQueries({ queryKey: hrmsKeys.salaryRevisions() });
        navigate(`/hr/salary-revisions/${editId}`);
      } else {
        const created = await hrmsService.createSalaryRevision(payload);
        message.success('Salary revision created');
        await queryClient.invalidateQueries({ queryKey: hrmsKeys.salaryRevisions() });
        navigate(`/hr/salary-revisions/${created?.id || ''}`);
      }
    } catch (e: unknown) {
      if ((e as any)?.errorFields) return;
      const err = e as { response?: { data?: { message?: string } }; message?: string };
      message.error(err?.response?.data?.message || err?.message || (isEdit ? 'Update failed' : 'Create failed'));
    } finally {
      setSaving(false);
    }
  };

  if (isEdit && existingLoading) {
    return (
      <WorkspaceLayout header={<EnterprisePageHeader documentLabel="Edit salary revision" />}>
        <Spin />
      </WorkspaceLayout>
    );
  }

  if (isEdit && (existingError || !existing)) {
    return (
      <WorkspaceLayout header={<EnterprisePageHeader documentLabel="Edit salary revision" />}>
        <Card>Salary revision not found</Card>
      </WorkspaceLayout>
    );
  }

  return (
    <WorkspaceLayout
      header={
        <EnterprisePageHeader
          documentLabel={isEdit ? 'Edit salary revision' : 'New salary revision'}
          documentNumber={isEdit ? existing?.revision_number : undefined}
          subtitle="Set amounts by salary head — applied structure drives payroll"
          primaryActions={
            <Space>
              <Button
                icon={<ArrowLeftOutlined />}
                onClick={() =>
                  navigate(isEdit && editId ? `/hr/salary-revisions/${editId}` : '/hr/salary-revisions')
                }
              >
                Cancel
              </Button>
              <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={() => void save()}>
                {isEdit ? 'Save changes' : 'Save'}
              </Button>
            </Space>
          }
        />
      }
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Salary by salary head"
        description={
          <span>
            Choose <Link to="/hr/salary-heads">Salary heads</Link> and set the new amount for each.
            {isEdit
              ? ' Draft revisions can be edited until submitted for approval.'
              : ' Current open components are prefilled when you pick an employee.'}{' '}
            Applying this revision closes the prior structure and opens these lines from the effective date.
          </span>
        }
      />
      <Form form={form} layout="vertical" initialValues={{ revision_type: 'Increment', lines: [] }}>
        <Card title="Revision" style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item name="employee_id" label="Employee" rules={[{ required: true }]}>
                <Select
                  options={empOptions}
                  showSearch
                  optionFilterProp="label"
                  disabled={isEdit}
                  onChange={() => {
                    if (!isEdit) form.setFieldsValue({ lines: [] });
                  }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="revision_type" label="Type" rules={[{ required: true }]}>
                <Select
                  options={['Increment', 'Promotion', 'Correction', 'Demotion', 'Other'].map((v) => ({
                    value: v,
                    label: v,
                  }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="effective_from" label="Effective from" rules={[{ required: true }]}>
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="new_grade_id" label="New grade (optional)">
                <Select
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  placeholder="Select grade"
                  options={gradeOptions}
                />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name="reason" label="Reason">
                <Input.TextArea rows={3} />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Card
          title="Salary heads"
          extra={
            <Text type="secondary">
              Previous total {previousTotal.toLocaleString()} → New total {newTotal.toLocaleString()}
            </Text>
          }
        >
          <Form.List
            name="lines"
            rules={[
              {
                validator: async (_, lines) => {
                  if (!lines || !lines.length) {
                    return Promise.reject(new Error('Add at least one salary head'));
                  }
                },
              },
            ]}
          >
            {(fields, { add, remove }, { errors }) => (
              <>
                <Table
                  size="small"
                  pagination={false}
                  rowKey="key"
                  dataSource={fields}
                  locale={{
                    emptyText: employeeId
                      ? 'No lines yet — add a salary head'
                      : 'Select an employee to prefill current structure',
                  }}
                  columns={[
                    {
                      title: 'Salary head',
                      key: 'head',
                      render: (_, field) => (
                        <Form.Item
                          {...field}
                          name={[field.name, 'salary_component_id']}
                          rules={[{ required: true, message: 'Select head' }]}
                          style={{ marginBottom: 0 }}
                          extra={
                            linesWatch[field.name]?.component_code ? (
                              <Text type="secondary">
                                <Text code>{linesWatch[field.name]?.component_code}</Text>
                                {linesWatch[field.name]?.component_name
                                  ? ` — ${linesWatch[field.name]?.component_name}`
                                  : ''}
                              </Text>
                            ) : null
                          }
                        >
                          <Select
                            loading={headsLoading}
                            showSearch
                            optionFilterProp="label"
                            placeholder="Select salary head"
                            options={headOptions.map((o) => ({
                              ...o,
                              disabled:
                                usedHeadIds.has(o.value) &&
                                linesWatch[field.name]?.salary_component_id !== o.value,
                            }))}
                            onChange={(id: string) => onHeadChange(field.name, id)}
                            notFoundContent={
                              headsLoading
                                ? 'Loading…'
                                : 'No earning salary heads — create them under Salary heads'
                            }
                          />
                        </Form.Item>
                      ),
                    },
                    {
                      title: 'Previous',
                      key: 'prev',
                      width: 130,
                      render: (_, field) => (
                        <>
                          <Form.Item {...field} name={[field.name, 'component_code']} hidden>
                            <Input />
                          </Form.Item>
                          <Form.Item {...field} name={[field.name, 'component_name']} hidden>
                            <Input />
                          </Form.Item>
                          <Form.Item
                            {...field}
                            name={[field.name, 'previous_amount']}
                            style={{ marginBottom: 0 }}
                          >
                            <InputNumber style={{ width: '100%' }} min={0} disabled />
                          </Form.Item>
                        </>
                      ),
                    },
                    {
                      title: 'New amount',
                      key: 'amount',
                      width: 140,
                      render: (_, field) => (
                        <Form.Item
                          {...field}
                          name={[field.name, 'amount']}
                          rules={[{ required: true, message: 'Amount required' }]}
                          style={{ marginBottom: 0 }}
                        >
                          <InputNumber style={{ width: '100%' }} min={0} />
                        </Form.Item>
                      ),
                    },
                    {
                      title: '',
                      key: 'rm',
                      width: 48,
                      render: (_, field) => (
                        <Button
                          type="text"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => remove(field.name)}
                          disabled={fields.length <= 1}
                        />
                      ),
                    },
                  ]}
                />
                <Button
                  type="dashed"
                  icon={<PlusOutlined />}
                  style={{ marginTop: 12 }}
                  disabled={!employeeId}
                  onClick={() =>
                    add({
                      salary_component_id: undefined,
                      component_code: undefined,
                      component_name: undefined,
                      previous_amount: 0,
                      amount: 0,
                    })
                  }
                >
                  Add salary head
                </Button>
                <Form.ErrorList errors={errors} />
              </>
            )}
          </Form.List>
        </Card>
      </Form>
    </WorkspaceLayout>
  );
};

export default SalaryRevisionFormPage;
